// src/pages/TeacherTimetable.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { ref, get } from 'firebase/database';

const TeacherTimetable = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [teacherTimetable, setTeacherTimetable] = useState(null);
  const [allTimetables, setAllTimetables] = useState({});
  const [debugInfo, setDebugInfo] = useState('');
  const [foundEntries, setFoundEntries] = useState(0);
  const [debugLogs, setDebugLogs] = useState([]);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const periods = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'];

  // Helper function to normalize text for matching
  const normalizeText = (value) => {
    return String(value || '').trim().toLowerCase();
  };

  // Helper function to normalize period key
  const normalizePeriod = (periodKey) => {
    // If periodKey is a number like 1, 2, 3 etc.
    if (typeof periodKey === 'number') {
      return `P${periodKey}`;
    }
    // If periodKey is a string like "1", "2", "3" etc.
    if (typeof periodKey === 'string' && /^\d+$/.test(periodKey)) {
      return `P${periodKey}`;
    }
    // If periodKey is already like "P1", "P2" etc.
    if (typeof periodKey === 'string' && periodKey.toUpperCase().startsWith('P')) {
      return periodKey.toUpperCase();
    }
    // Default: return as is
    return String(periodKey);
  };

  // Helper function to get display class name
  const getDisplayClassName = (classKey) => {
    return classKey.replace(/_/g, ' ');
  };

  // Check admin authentication
  useEffect(() => {
    const isAdminLoggedIn = sessionStorage.getItem('isAdminLoggedIn');
    if (!isAdminLoggedIn) {
      navigate('/login');
    }
  }, [navigate]);

  // Load teachers and timetables
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [teachersSnap, timetablesSnap] = await Promise.all([
          get(ref(db, 'teachers')),
          get(ref(db, 'timetables'))
        ]);

        if (teachersSnap.exists()) {
          const teachersData = teachersSnap.val();
          const activeTeachers = Object.values(teachersData).filter(t => t.status !== 'inactive');
          setTeachers(activeTeachers);
        }

        if (timetablesSnap.exists()) {
          const timetablesData = timetablesSnap.val();
          console.log('📚 FULL TIMETABLE DATABASE:', timetablesData);
          setAllTimetables(timetablesData);

          // Debug: Log the structure of first class
          const classKeys = Object.keys(timetablesData);
          if (classKeys.length > 0) {
            const firstClass = classKeys[0];
            console.log(`📖 Structure of ${firstClass}:`, timetablesData[firstClass]);
            
            // Log one day's data to understand structure
            const daysInClass = Object.keys(timetablesData[firstClass]);
            if (daysInClass.length > 0) {
              const firstDay = daysInClass[0];
              console.log(`📅 Structure of ${firstClass} - ${firstDay}:`, timetablesData[firstClass][firstDay]);
              
              // Log one period's data
              const periodsInDay = Object.keys(timetablesData[firstClass][firstDay]);
              if (periodsInDay.length > 0) {
                const firstPeriod = periodsInDay[0];
                console.log(`⏰ Structure of ${firstClass} - ${firstDay} - ${firstPeriod}:`, 
                  timetablesData[firstClass][firstDay][firstPeriod]);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate teacher timetable when teacher is selected
  useEffect(() => {
    if (!selectedTeacher || !allTimetables || Object.keys(allTimetables).length === 0) {
      setTeacherTimetable(null);
      setFoundEntries(0);
      setDebugInfo('');
      setDebugLogs([]);
      return;
    }

    console.log(`🔍 Building timetable for teacher: "${selectedTeacher}"`);
    console.log(`📋 Available classes:`, Object.keys(allTimetables));

    // Initialize empty timetable
    const timetable = {};
    days.forEach(day => {
      timetable[day] = {};
      periods.forEach(period => {
        timetable[day][period] = null;
      });
    });

    let entryCount = 0;
    const logs = [];
    const normalizedSelected = normalizeText(selectedTeacher);

    // Scan ALL classes
    Object.entries(allTimetables).forEach(([classKey, classData]) => {
      if (!classData || typeof classData !== 'object') {
        console.log(`⚠️ No valid data for class: ${classKey}`);
        return;
      }

      console.log(`📚 Scanning class: ${classKey}`);

      // Scan ALL days in this class
      Object.entries(classData).forEach(([day, dayData]) => {
        if (!dayData || typeof dayData !== 'object') {
          console.log(`⚠️ No valid data for day: ${day} in class ${classKey}`);
          return;
        }

        console.log(`📅 Scanning day: ${day} in class ${classKey}`);

        // Scan ALL periods in this day
        Object.entries(dayData).forEach(([periodKey, periodData]) => {
          if (!periodData || typeof periodData !== 'object') {
            console.log(`⚠️ No valid data for period: ${periodKey} in ${classKey} on ${day}`);
            return;
          }

          console.log(`⏰ Checking period: ${periodKey} in ${classKey} on ${day}`);
          console.log(`📝 Period data:`, periodData);

          // Extract teacher name - handle different possible structures
          let savedTeacher = null;
          let subject = null;

          // Check different possible structures
          if (periodData.teacher) {
            savedTeacher = periodData.teacher;
          } else if (periodData.teacherName) {
            savedTeacher = periodData.teacherName;
          } else if (periodData.teacher && typeof periodData.teacher === 'object') {
            savedTeacher = periodData.teacher.name || periodData.teacher.teacherName;
          }

          // Extract subject
          if (periodData.subject) {
            subject = periodData.subject;
          } else if (periodData.subjectName) {
            subject = periodData.subjectName;
          } else if (periodData.subject && typeof periodData.subject === 'object') {
            subject = periodData.subject.name || periodData.subject.subjectName;
          }

          console.log(`👨‍🏫 Saved teacher: "${savedTeacher}", Selected: "${selectedTeacher}"`);
          console.log(`📖 Subject: "${subject}"`);

          // Normalize and compare
          const normalizedSaved = normalizeText(savedTeacher);
          const isMatch = normalizedSaved === normalizedSelected;

          console.log(`🔍 Match: ${isMatch} (${normalizedSaved} === ${normalizedSelected})`);

          if (isMatch && savedTeacher) {
            // Normalize period for display
            const normalizedPeriod = normalizePeriod(periodKey);
            
            // Check if this period is valid
            if (days.includes(day) && periods.includes(normalizedPeriod)) {
              entryCount++;
              timetable[day][normalizedPeriod] = {
                class: getDisplayClassName(classKey),
                subject: subject || 'Unknown Subject',
                originalPeriod: periodKey,
                originalDay: day,
                originalClass: classKey
              };

              console.log(`✅ MATCH FOUND: ${selectedTeacher} → ${classKey} → ${day} → ${normalizedPeriod} (${subject})`);
              
              logs.push({
                class: getDisplayClassName(classKey),
                day: day,
                period: normalizedPeriod,
                subject: subject || 'Unknown Subject',
                originalPeriod: periodKey
              });
            } else {
              console.log(`⚠️ Period ${normalizedPeriod} not in standard periods list`);
            }
          }
        });
      });
    });

    console.log(`📊 Total entries found for "${selectedTeacher}": ${entryCount}`);
    
    // Sort logs by day and period
    const dayOrder = { Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5 };
    const periodOrder = { P1: 0, P2: 1, P3: 2, P4: 3, P5: 4, P6: 5, P7: 6 };
    
    logs.sort((a, b) => {
      if (dayOrder[a.day] !== dayOrder[b.day]) {
        return dayOrder[a.day] - dayOrder[b.day];
      }
      return periodOrder[a.period] - periodOrder[b.period];
    });

    setFoundEntries(entryCount);
    setTeacherTimetable(timetable);
    setDebugLogs(logs);
    
    if (entryCount === 0) {
      setDebugInfo(`❌ No timetable entries found for "${selectedTeacher}". 
        Please check:
        1. The teacher name matches exactly with the timetable data
        2. The teacher has assigned periods in any class timetable
        3. Check the console logs for detailed debugging information`);
    } else {
      setDebugInfo(`✅ Found ${entryCount} timetable entries for "${selectedTeacher}"`);
    }
  }, [selectedTeacher, allTimetables]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.removeItem('isAdminLoggedIn');
    navigate('/login');
  };

  // Go back to dashboard
  const handleBack = () => {
    navigate('/admin');
  };

  // Print functionality
  const handlePrint = () => {
    const printContent = document.getElementById('teacher-print');
    if (!printContent) return;

    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent.outerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  // WhatsApp functionality
  const getWhatsAppMessage = () => {
    if (!teacherTimetable || !selectedTeacher) return '';

    let message = 'MIS INTERNATIONAL SCHOOL\n';
    message += 'TEACHER TIMETABLE\n\n';
    message += `Teacher: ${selectedTeacher}\n`;
    message += '='.repeat(30) + '\n\n';

    days.forEach(day => {
      message += `${day.toUpperCase()}\n`;
      message += '-'.repeat(20) + '\n';
      periods.forEach(period => {
        const entry = teacherTimetable[day][period];
        if (entry) {
          message += `${period} - ${entry.class} - ${entry.subject}\n`;
        } else {
          message += `${period} - Free\n`;
        }
      });
      message += '\n';
    });

    message += '='.repeat(30) + '\n';
    message += 'Generated by School Timetable System\n';
   
    
    return message;
  };

  const handleWhatsApp = (isBusiness = false) => {
    const message = getWhatsAppMessage();
    if (!message) return;

    const encodedMessage = encodeURIComponent(message);
    const url = isBusiness
      ? `https://api.whatsapp.com/send?phone=&text=${encodedMessage}`
      : `https://wa.me/?text=${encodedMessage}`;
    window.open(url, '_blank');
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={handleBack} style={styles.backBtn}>← Back</button>
          <h1 style={styles.headerTitle}>👨‍🏫 Teacher Timetable</h1>
        </div>
        <button onClick={handleLogout} style={styles.logoutBtn}>
          🚪 Logout
        </button>
      </header>

      {/* Main Content */}
      <main style={styles.mainContent}>
        {/* Controls */}
        <div style={styles.controls}>
          <div style={styles.selectorGroup}>
            <label style={styles.label}>Select Teacher</label>
            <select
              value={selectedTeacher}
              onChange={(e) => setSelectedTeacher(e.target.value)}
              style={styles.select}
            >
              <option value="">-- Select a Teacher --</option>
              {teachers.map((teacher, index) => (
                <option key={index} value={teacher.name}>
                  {teacher.name}
                </option>
              ))}
            </select>
          </div>

          {selectedTeacher && debugInfo && (
            <div style={{...styles.debugInfo, ...(foundEntries === 0 ? styles.debugInfoError : styles.debugInfoSuccess)}}>
              {debugInfo}
            </div>
          )}

          {selectedTeacher && teacherTimetable && foundEntries > 0 && (
            <div style={styles.actionButtons}>
              <button onClick={handlePrint} style={styles.btnPrint}>
                🖨 Print
              </button>
              <button onClick={() => handleWhatsApp(false)} style={styles.btnWhatsapp}>
                📱 WhatsApp
              </button>
              <button onClick={() => handleWhatsApp(true)} style={styles.btnWhatsappBusiness}>
                💼 WhatsApp Business
              </button>
            </div>
          )}
        </div>

        {/* Debug Logs */}
        {selectedTeacher && debugLogs.length > 0 && (
          <div style={styles.debugLogsContainer}>
            <details style={styles.debugDetails}>
              <summary style={styles.debugSummary}>
                📋 Show Matching Entries ({debugLogs.length} found)
              </summary>
              <div style={styles.debugLogsList}>
                {debugLogs.map((log, index) => (
                  <div key={index} style={styles.debugLogItem}>
                    <span style={styles.debugLogClass}>{log.class}</span>
                    <span style={styles.debugLogDay}>{log.day}</span>
                    <span style={styles.debugLogPeriod}>{log.period}</span>
                    <span style={styles.debugLogSubject}>{log.subject}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

        {/* Timetable Display */}
        {selectedTeacher && teacherTimetable && foundEntries > 0 && (
          <div id="teacher-print" style={styles.timetableDisplay}>
            <div style={styles.timetableHeader}>
              <h2 style={styles.schoolName}>MIS INTERNATIONAL SCHOOL</h2>
              <h3 style={styles.timetableTitle}>TEACHER TIMETABLE</h3>
              <p style={styles.teacherName}>Teacher: {selectedTeacher}</p>
              <p style={styles.periodInfo}>Weekly Timetable</p>
            </div>

            <div style={styles.timetableGrid}>
              {days.map(day => (
                <div key={day} style={styles.dayColumn}>
                  <div style={styles.dayHeader}>{day}</div>
                  {periods.map(period => {
                    const entry = teacherTimetable[day][period];
                    return (
                      <div key={period} style={{...styles.periodCell, ...(entry ? styles.occupied : styles.free)}}>
                        <div style={styles.periodLabel}>{period}</div>
                        {entry ? (
                          <div style={styles.periodDetails}>
                            <div style={styles.className}>{entry.class}</div>
                            <div style={styles.subjectName}>{entry.subject}</div>
                          </div>
                        ) : (
                          <div style={styles.freeLabel}>FREE</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div style={styles.timetableFooter}>
              <p>Generated by Timetable Management System</p>
            </div>
          </div>
        )}

        {/* No Data Message */}
        {selectedTeacher && foundEntries === 0 && !loading && (
          <div style={styles.noDataMessage}>
            <div style={styles.noDataIcon}>🔍</div>
            <h3>No Timetable Found</h3>
            <p>No timetable entries found for <strong>{selectedTeacher}</strong></p>
            <p style={styles.noDataHint}>
              Please check the following:
              <br />
              1. The teacher name matches exactly with the timetable data
              <br />
              2. The teacher has assigned periods in any class timetable
              <br />
              3. Open the browser console (F12) to see detailed logs
            </p>
          </div>
        )}

        {/* Select Teacher Prompt */}
        {!selectedTeacher && (
          <div style={styles.selectPrompt}>
            <div style={styles.selectPromptIcon}>👨‍🏫</div>
            <h3>Select a Teacher</h3>
            <p>Choose a teacher from the dropdown above to view their timetable</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p>Powered by Jauhari WebTech</p>
        <p>© 2026 Jauhari WebTech. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f5f7fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
  },

  // Header
  header: {
    background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1b4e 100%)',
    color: 'white',
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  backBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '6px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },
  headerTitle: {
    fontSize: '18px',
    margin: 0,
  },
  logoutBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '8px 18px',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },

  // Main Content
  mainContent: {
    flex: 1,
    padding: '15px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },

  // Controls
  controls: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  selectorGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '15px',
  },
  label: {
    fontWeight: 600,
    color: '#2c3e50',
    fontSize: '14px',
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    background: 'white',
    WebkitAppearance: 'none',
    appearance: 'none',
  },
  debugInfo: {
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '15px',
    borderLeft: '3px solid #2d1b4e',
    whiteSpace: 'pre-line',
  },
  debugInfoSuccess: {
    background: '#f0f8ff',
    borderLeftColor: '#22c55e',
    color: '#155724',
  },
  debugInfoError: {
    background: '#fff5f5',
    borderLeftColor: '#ef4444',
    color: '#721c24',
  },
  actionButtons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  btnPrint: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 15px',
    background: '#2c3e50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  btnWhatsapp: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 15px',
    background: '#25D366',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  btnWhatsappBusiness: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 15px',
    background: '#075E54',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },

  // Debug Logs
  debugLogsContainer: {
    background: 'white',
    borderRadius: '12px',
    padding: '15px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  debugDetails: {
    cursor: 'pointer',
  },
  debugSummary: {
    fontWeight: 600,
    color: '#2c3e50',
    fontSize: '14px',
    padding: '5px 0',
  },
  debugLogsList: {
    marginTop: '10px',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  debugLogItem: {
    display: 'grid',
    gridTemplateColumns: '2fr 1.5fr 1fr 2fr',
    gap: '8px',
    padding: '6px 10px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '12px',
  },
  debugLogClass: {
    fontWeight: 600,
    color: '#2c3e50',
  },
  debugLogDay: {
    color: '#7f8c8d',
  },
  debugLogPeriod: {
    color: '#2d1b4e',
    fontWeight: 500,
  },
  debugLogSubject: {
    color: '#666',
  },

  // Timetable Display
  timetableDisplay: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  timetableHeader: {
    textAlign: 'center',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0',
    marginBottom: '15px',
  },
  schoolName: {
    margin: 0,
    fontSize: '20px',
    color: '#2c3e50',
  },
  timetableTitle: {
    margin: '4px 0',
    fontSize: '16px',
    color: '#7f8c8d',
  },
  teacherName: {
    margin: '4px 0 0 0',
    fontSize: '15px',
    fontWeight: 600,
    color: '#2c3e50',
  },
  periodInfo: {
    margin: '2px 0 0 0',
    fontSize: '12px',
    color: '#95a5a6',
  },
  timetableGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '6px',
    overflowX: 'auto',
  },
  dayColumn: {
    minWidth: '80px',
  },
  dayHeader: {
    background: '#2d1b4e',
    color: 'white',
    padding: '8px 4px',
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '12px',
    borderRadius: '6px 6px 0 0',
  },
  periodCell: {
    background: '#f8f9fa',
    padding: '8px 4px',
    minHeight: '55px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontSize: '10px',
  },
  occupied: {
    background: '#f0f4ff',
  },
  free: {
    background: '#fafafa',
  },
  periodLabel: {
    fontWeight: 600,
    color: '#7f8c8d',
    fontSize: '9px',
  },
  periodDetails: {
    marginTop: '2px',
  },
  className: {
    fontWeight: 600,
    color: '#2c3e50',
    fontSize: '10px',
  },
  subjectName: {
    color: '#2d1b4e',
    fontSize: '9px',
  },
  freeLabel: {
    color: '#bdc3c7',
    fontSize: '9px',
    fontStyle: 'italic',
  },
  timetableFooter: {
    textAlign: 'center',
    paddingTop: '15px',
    borderTop: '2px solid #f0f0f0',
    marginTop: '15px',
  },

  // No Data Message
  noDataMessage: {
    background: 'white',
    borderRadius: '12px',
    padding: '40px 20px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  noDataIcon: {
    fontSize: '48px',
    marginBottom: '10px',
  },
  noDataHint: {
    fontSize: '13px',
    color: '#95a5a6',
    marginTop: '10px',
    lineHeight: '1.6',
  },

  // Select Prompt
  selectPrompt: {
    background: 'white',
    borderRadius: '12px',
    padding: '60px 20px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  selectPromptIcon: {
    fontSize: '64px',
    marginBottom: '15px',
  },

  // Footer
  footer: {
    background: 'white',
    textAlign: 'center',
    padding: '12px',
    borderTop: '1px solid #e0e0e0',
    marginTop: '20px',
  },
};

// Add print styles
const printStyleSheet = document.createElement("style");
printStyleSheet.textContent = `
  @media print {
    body * {
      visibility: hidden;
    }
    #teacher-print, #teacher-print * {
      visibility: visible;
    }
    #teacher-print {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      padding: 20px !important;
    }
    .teacher-timetable-grid {
      display: grid !important;
      grid-template-columns: repeat(7, 1fr) !important;
      gap: 4px !important;
    }
    .period-cell {
      min-height: 40px !important;
      padding: 4px !important;
      font-size: 8px !important;
    }
    .day-header {
      font-size: 10px !important;
      padding: 4px !important;
    }
    .school-name {
      font-size: 16px !important;
    }
    .timetable-title {
      font-size: 12px !important;
    }
    .teacher-name {
      font-size: 12px !important;
    }
    .occupied {
      background: #f0f4ff !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .day-header {
      background: #2d1b4e !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;
document.head.appendChild(printStyleSheet);

export default TeacherTimetable;